import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {UppercaseLabel} from 'shells/typography/uppercase-label';

import type {BillingInfo} from 'nutshell-graphql-types';

import {
    useUpdateBillingInfo,
    useAutofillBillingInfo,
    useGetBillingDefaults,
} from '../../graphql/hooks';

import {AddressFields} from './address-fields';
import {AgreementCheckbox} from './agreement-checkbox';
import {
    defaultAddressForm,
    defaultBankForm,
    defaultCardForm,
    defaultBaseFormInfo,
    cardValidationFields,
    bankValidationFields,
    addressValidationFields,
    formValidationFields,
} from './consts';
import {checkFormValidity, getHasError} from './helpers';
import {PaymentSection} from './payment-section';
import type {
    AddressForm,
    AddressValidation,
    BankForm,
    BankValidation,
    BaseFormDataValidation,
    BaseFormInfo,
    CardForm,
    CardValidation,
    FormChangeInfo,
    RecurlyCardElementState,
} from './types';

import styles from './billing-form.module.css';

export type Props = {
    billingInfo: BillingInfo | null | undefined;
    isLoadingBillingInfo: boolean;
    refetchBillingInfo: () => Promise<any>;
    closeForm: () => void;
    setHasUnsavedBillingChanges?: (arg1: boolean) => void;
};

export function BillingForm(props: Props) {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [isSavingPaymentInfo, setIsSavingPaymentInfo] = React.useState<boolean>(false);
    const [isAutofillingPaymentInfo, setIsAutofillingPaymentInfo] = React.useState<boolean>(false);
    const [isElementCardFocused, setIsElementCardFocused] = React.useState<boolean>(false);
    const [recurlyPaymentErrors, setRecurlyPaymentErrors] = React.useState<string[]>([]);
    const [cardValidation, setCardValidation] = React.useState<CardValidation | null | undefined>(
        null
    );
    const [bankValidation, setBankValidation] = React.useState<BankValidation | null | undefined>(
        null
    );
    const [addressValidation, setAddressValidation] = React.useState<
        AddressValidation | null | undefined
    >(null);
    const [formValidation, setFormValidation] = React.useState<
        BaseFormDataValidation | null | undefined
    >(null);
    const [cardForm, setCardForm] = React.useState<CardForm>(defaultCardForm);
    const [bankForm, setBankForm] = React.useState<BankForm>(defaultBankForm);
    const [addressForm, setAddressForm] = React.useState<AddressForm>(defaultAddressForm);
    const [baseForm, setBaseForm] = React.useState<BaseFormInfo>(defaultBaseFormInfo);
    const [isEditingPaymentInfo, setIsEditingPaymentInfo] = React.useState<boolean>(false);
    const [isFormValid, setIsFormValid] = React.useState<boolean>(false);

    // @ts-expect-error
    const recurly = window.recurly;
    // @ts-expect-error
    const recurlyConfig = window.RecurlyConfig;
    // https://developers.recurly.com/reference/recurly-js/#elements
    const recurlyManagedElements = React.useRef(recurly.Elements());
    const {updateBillingInfo} = useUpdateBillingInfo();
    const {autofillBillingInfo} = useAutofillBillingInfo();
    const {billingDefaults, isLoading: isLoadingBillingDefaults} = useGetBillingDefaults(
        Boolean(props.billingInfo)
    );

    // Ensure we get correct prohibitAchBilling value for trials with no billing info
    const prohibitAchBilling = NutClientConfig.prohibitAchBilling;

    const shouldShowCreditCardForm =
        (baseForm && baseForm.paymentType === 'card') || prohibitAchBilling;

    React.useEffect(() => {
        if (!recurly.configured) {
            recurly.configure({
                publicKey: recurlyConfig.publicApiKey,
                fraud: {
                    kount: {dataCollector: true},
                },
            });
        }
    }, [recurly, recurlyConfig]);

    React.useEffect(() => {
        const recurlyCreditCardElement = recurlyManagedElements.current.CardElement({
            style: {
                fontSize: '13px',
                fontColor: colors.greyDk,
                fontFamily: 'IBM Plex Sans',
                placeholder: {
                    color: colors.placeholder,
                    content: {
                        number: 'Card number',
                        cvv: 'CVC',
                    },
                },
                invalid: {
                    fontColor: colors.rose,
                },
            },
        });

        recurlyCreditCardElement.attach('#recurly-credit-card-elements');

        // Register event listener for all changes to that element
        recurlyCreditCardElement.on('change', handleCardElementChange);

        return () => recurlyCreditCardElement.off('change', handleCardElementChange);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recurly]);

    React.useEffect(() => {
        // Try to autofill the billing info if there is no billing info
        if (billingDefaults && !props.billingInfo) {
            props.setHasUnsavedBillingChanges?.(true);

            setBaseForm({
                ...baseForm,
                companyName: billingDefaults.companyName || '',
                email: billingDefaults.email || '',
            });
            setAddressForm({
                ...addressForm,
                address1: (billingDefaults.address && billingDefaults.address.address1) || '',
                address2: (billingDefaults.address && billingDefaults.address.address2) || '',
                city: (billingDefaults.address && billingDefaults.address.city) || '',
                state: (billingDefaults.address && billingDefaults.address.state) || '',
                postal_code: (billingDefaults.address && billingDefaults.address.postal) || '',
                country: (billingDefaults.address && billingDefaults.address.country) || 'US',
            });
            setCardForm({
                ...cardForm,
                first_name: billingDefaults.firstName || '',
                last_name: billingDefaults.lastName || '',
            });
        }

        // If there is billing info, set the form to the billing info
        if (props.billingInfo) {
            const {cardInfo, bankInfo, addressInfo, ...otherInfo} = props.billingInfo;

            if (cardInfo) {
                setCardForm({
                    first_name: cardInfo.firstName,
                    last_name: cardInfo.lastName,
                    number: 'present',
                    month: 'present',
                    year: 'present',
                    cvv: 'present',
                });
                setCardValidation({
                    first_name: true,
                    last_name: true,
                    number: true,
                    month: true,
                    year: true,
                    cvv: true,
                });
            }

            if (bankInfo) {
                setBankForm({
                    routing_number: bankInfo.routingNumber,
                    account_number: bankInfo.accountNumber,
                    account_number_confirmation: bankInfo.accountNumber,
                    account_type: bankInfo.accountType,
                    name_on_account: bankInfo.nameOnAccount,
                });
                setBankValidation({
                    routing_number: true,
                    account_number: true,
                    account_number_confirmation: true,
                    account_type: true,
                    name_on_account: true,
                });
            }

            setBaseForm({
                email: otherInfo.email || '',
                companyName: otherInfo.companyName || '',
                paymentType: !prohibitAchBilling && bankInfo ? 'bank' : 'card',
                agree: true,
            });
            // Don't want to validate paymentType since it won't exist if they don't change the toggle
            setFormValidation({
                email: true,
                companyName: true,
                agree: true,
            });

            setAddressForm({
                phone: otherInfo.phoneNumber || '',
                address1: addressInfo && addressInfo.address1 ? addressInfo.address1 : '',
                address2: addressInfo && addressInfo.address2 ? addressInfo.address2 : '',
                city: addressInfo && addressInfo.city ? addressInfo.city : '',
                state: addressInfo && addressInfo.state ? addressInfo.state : '',
                postal_code: addressInfo && addressInfo.postal ? addressInfo.postal : '',
                country: addressInfo && addressInfo.country ? addressInfo.country : 'US',
            });
            setAddressValidation({
                phone: true,
                address1: true,
                city: true,
                state: true,
                postal_code: true,
                country: true,
            });
        } else {
            // Form default to include the instance name and agree to terms
            // @ts-expect-error
            setFormValidation({...formValidation, companyName: true, agree: true});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.billingInfo, billingDefaults]);

    const handleChange = (changedInfo: FormChangeInfo) => {
        props.setHasUnsavedBillingChanges?.(true);

        const {name: formName, field, value} = changedInfo;
        // address2 field is always valid since it's not required
        const fieldIsValid =
            typeof value === 'string' ? value.length > 0 || field === 'address2' : value;

        if (formName === 'addressForm') {
            setAddressForm({...addressForm, [field]: value});
            // @ts-expect-error
            setAddressValidation({...addressValidation, [field]: fieldIsValid});
        } else if (formName === 'bankForm') {
            setBankForm({...bankForm, [field]: value});
            // @ts-expect-error
            setBankValidation({...bankValidation, [field]: fieldIsValid});
        } else if (formName === 'baseForm') {
            setBaseForm({...baseForm, [field]: value});
            // @ts-expect-error
            setFormValidation({
                ...formValidation,
                [field]: Boolean(value === true || fieldIsValid),
            });
        } else if (formName === 'cardForm') {
            setCardForm({...cardForm, [field]: value});
            // @ts-expect-error
            setCardValidation({...cardValidation, [field]: fieldIsValid});
        }
    };

    const handleCardElementChange = (cardState: RecurlyCardElementState) => {
        // @ts-expect-error
        setCardValidation({
            ...cardValidation,
            // If the validation is previously invalid, we’ll set it to Recurly’s
            // CardElement's internal validation state. If it's already valid,
            // just keep it as "true"
            number: getHasError(cardValidation, 'number') ? cardState.number.valid : true,
            month: getHasError(cardValidation, 'month') ? cardState.expiry.valid : true,
            year: getHasError(cardValidation, 'year') ? cardState.expiry.valid : true,
            cvv: getHasError(cardValidation, 'cvv') ? cardState.cvv.valid : true,
        });

        setIsElementCardFocused(cardState.focus);
    };

    const handleRecurlyTokenSubmission = (error?: any | null, token?: any | null) => {
        if (error) {
            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.track(JSON.stringify(error));
            }
            if (error && error.fields) {
                setIsSaving(false);

                // @ts-expect-error
                error.fields.forEach((field) => {
                    if (cardValidationFields.includes(field)) {
                        // @ts-expect-error
                        setCardValidation({...cardValidation, [field]: false});
                    } else if (bankValidationFields.includes(field)) {
                        // @ts-expect-error
                        setBankValidation({...bankValidation, [field]: false});
                    } else if (addressValidationFields.includes(field)) {
                        // @ts-expect-error
                        setAddressValidation({...addressValidation, [field]: false});
                    } else if (formValidationFields.includes(field)) {
                        // @ts-expect-error
                        setFormValidation({...formValidation, [field]: false});
                    }
                });
            }
        } else if (token) {
            setIsSavingPaymentInfo(true);

            handleUpdateBillingInfo(token.id)
                .then(handleSuccess)
                .catch((e) => {
                    handleError(e);
                });
        }
    };

    const handleUpdatePaymentInfoThenSubmit = (e?: React.SyntheticEvent<any>) => {
        if (e) {
            e.preventDefault();
        }
        setRecurlyPaymentErrors([]);
        setIsSaving(true);
        props.setHasUnsavedBillingChanges?.(false);

        if (baseForm && baseForm.paymentType === 'bank') {
            // Recurly does not like anything other than digits (only) when
            // submitting a 'bank' form.
            const cleanPhone = ((addressForm && addressForm.phone) || '').replace(/\D/g, '');
            const tokenData = {...bankForm, ...addressForm, phone: cleanPhone} as const;
            // @ts-expect-error
            recurly.bankAccount.token(tokenData, (err, token) =>
                handleRecurlyTokenSubmission(err, token)
            );
        } else {
            const tokenData = {...cardForm, ...addressForm} as const;
            // This function takes three parameters if we want recurly
            // to register values from their built in elements
            // @ts-expect-error
            recurly.token(recurlyManagedElements.current, tokenData, (err, token) =>
                handleRecurlyTokenSubmission(err, token)
            );
        }
    };

    const handleUpdateBillingInfo = (tokenId?: string) => {
        return updateBillingInfo({
            recurlyToken: tokenId,
            paymentType: baseForm.paymentType || 'card',
            agree: baseForm.agree,
            addressInfo: {
                address1: addressForm.address1,
                address2: addressForm.address2,
                city: addressForm.city,
                state: addressForm.state,
                postalCode: addressForm.postal_code,
                country: addressForm.country,
            },
            contactInfo: {
                email: baseForm.email,
                firstName: cardForm.first_name,
                lastName: cardForm.last_name,
                companyName: baseForm.companyName,
                phone: addressForm.phone,
            },
        });
    };

    const handleUpdateBillingInfoOnly = (e: React.SyntheticEvent<any>) => {
        props.setHasUnsavedBillingChanges?.(false);
        e.preventDefault();
        setRecurlyPaymentErrors([]);
        setIsSaving(true);

        handleUpdateBillingInfo()
            .then(handleSuccess)
            .catch((error) => {
                handleError(error);
            });
    };

    const handleAutofillBillingInfo = (e: React.SyntheticEvent<any>) => {
        props.setHasUnsavedBillingChanges?.(false);
        e.preventDefault();
        setRecurlyPaymentErrors([]);
        setIsAutofillingPaymentInfo(true);

        autofillBillingInfo({
            firstName: cardForm.first_name,
            lastName: cardForm.last_name,
            companyName: baseForm.companyName,
            email: baseForm.email,
            phone: addressForm.phone,
        })
            .then(handleSuccess)
            .catch((error) => {
                handleError(error);
            });
    };

    const handleSuccess = () => {
        setRecurlyPaymentErrors([]);
        setIsEditingPaymentInfo(false);
        setIsSaving(false);
        setIsAutofillingPaymentInfo(false);
        props.refetchBillingInfo().then(() => {
            props.closeForm();
            setIsSavingPaymentInfo(false);
        });
    };

    // @ts-expect-error
    const handleError = (error) => {
        const errorMessage =
            safelyGetGraphQLErrorMessage(error) ||
            'Error communicating with billing system. Please contact support.';
        setRecurlyPaymentErrors([errorMessage]);

        setIsSavingPaymentInfo(false);
        setIsAutofillingPaymentInfo(false);
        setIsSaving(false);
    };

    const hasExistingPaymentInfo = Boolean(props.billingInfo);

    const handleSubmit = hasExistingPaymentInfo
        ? handleUpdateBillingInfoOnly
        : handleUpdatePaymentInfoThenSubmit;

    const handleCancel = () => {
        props.closeForm();
    };

    React.useEffect(() => {
        // This uses a tested helper function to determine if the form is valid
        const isValid = checkFormValidity({...cardForm, ...bankForm, ...addressForm, ...baseForm});

        setIsFormValid(isValid);
    }, [bankForm, cardForm, addressForm, baseForm]);

    const isUpdateButtonDisabled = !isFormValid || isEditingPaymentInfo;
    const updateButtonTooltip = isUpdateButtonDisabled
        ? 'Please finish updating your billing information'
        : '';

    return (
        <Card hasBorder={true}>
            <div className='pad-16'>
                <StackLegacy spacing={16}>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.header}>
                            <UppercaseLabel>Billing information</UppercaseLabel>
                            <div className='flex gap-12 justify-end'>
                                <Button
                                    variant='text-secondary'
                                    onClick={handleCancel}
                                    noPadding={true}
                                >
                                    Cancel
                                </Button>
                                <SaveButton
                                    errorSaving={recurlyPaymentErrors.length > 0}
                                    buttonText='Update'
                                    disabled={isUpdateButtonDisabled}
                                    savingText='Submitting'
                                    tooltipText={updateButtonTooltip}
                                    isSaving={isEditingPaymentInfo ? false : isSaving}
                                    // @ts-expect-error
                                    onClick={handleSubmit}
                                    resetOnComplete={true}
                                    size='small'
                                />
                                {NutClientConfig.isDevelopment && !hasExistingPaymentInfo && (
                                    <SaveButton
                                        errorSaving={recurlyPaymentErrors.length > 0}
                                        buttonText='Autofill'
                                        savingText='Autofilling'
                                        isSaving={
                                            isEditingPaymentInfo ? false : isAutofillingPaymentInfo
                                        }
                                        // @ts-expect-error
                                        onClick={handleAutofillBillingInfo}
                                        resetOnComplete={true}
                                        size='small'
                                    />
                                )}
                            </div>
                        </div>
                        <StackLegacy spacing={16}>
                            <PaymentSection
                                paymentType={
                                    !prohibitAchBilling && baseForm && baseForm.paymentType
                                        ? baseForm.paymentType
                                        : 'card'
                                }
                                onUpdateField={handleChange}
                                prohibitAchBilling={prohibitAchBilling}
                                cardForm={cardForm}
                                cardValidation={cardValidation}
                                bankForm={bankForm}
                                bankValidation={bankValidation}
                                recurlyPaymentErrors={recurlyPaymentErrors}
                                isElementCardFocused={isElementCardFocused}
                                shouldShowCreditCardForm={shouldShowCreditCardForm}
                                // @ts-expect-error
                                billingInfo={props.billingInfo}
                                onUpdate={handleUpdatePaymentInfoThenSubmit}
                                cardInfo={props.billingInfo && props.billingInfo.cardInfo}
                                bankInfo={props.billingInfo && props.billingInfo.bankInfo}
                                isEditingPaymentInfo={isEditingPaymentInfo}
                                setIsEditingPaymentInfo={setIsEditingPaymentInfo}
                                isLoadingPaymentInfo={
                                    props.isLoadingBillingInfo || isLoadingBillingDefaults
                                }
                                isSavingPaymentInfo={isSavingPaymentInfo}
                                isSaveDisabled={!isFormValid}
                            />
                            <AddressFields
                                baseForm={baseForm}
                                formValidation={formValidation}
                                addressForm={addressForm}
                                addressValidation={addressValidation}
                                onUpdateBaseField={(field, value) => {
                                    handleChange({name: 'baseForm', field, value});
                                }}
                                onUpdateField={(field, value) => {
                                    handleChange({name: 'addressForm', field, value});
                                }}
                            />
                            <div className='mt-16'>
                                <AgreementCheckbox
                                    doesAgree={Boolean(baseForm && baseForm.agree)}
                                    onUpdateField={(value) => {
                                        handleChange({name: 'baseForm', field: 'agree', value});
                                    }}
                                />
                            </div>
                            <div className='flex gap-12 justify-end'>
                                <Button
                                    variant='text-secondary'
                                    onClick={handleCancel}
                                    noPadding={true}
                                >
                                    Cancel
                                </Button>
                                <SaveButton
                                    buttonText='Update'
                                    errorSaving={recurlyPaymentErrors.length > 0}
                                    disabled={isUpdateButtonDisabled}
                                    tooltipText={updateButtonTooltip}
                                    savingText='Submitting'
                                    isSaving={isEditingPaymentInfo ? false : isSaving}
                                    // @ts-expect-error
                                    onClick={handleSubmit}
                                    resetOnComplete={true}
                                    size='small'
                                />
                            </div>
                        </StackLegacy>
                    </form>
                </StackLegacy>
            </div>
        </Card>
    );
}
